// Popup modal housing a two-scale (Price / Volume) Recharts graph for a single
// asset, opened by clicking a symbol in the AssetTable. History is fetched
// on-demand via the useAssetChart TanStack Query hook (backend proxies ~800
// trading days from FMP), then sliced client-side by the 1Y | 2Y | 3Y | YTD
// range toggle.
//
// Chart layout mirrors the FX / crypto sibling pop-overs:
//   - Close price -> Area on the LEFT price axis.
//   - Volume      -> Bars on the RIGHT volume axis (hidden when the feed
//                    reports no turnover, which is common for FMP indices).
//
// Modal behaviour (backdrop click-to-close, Escape-to-close, body scroll lock)
// matches the platform's FXDetailPopup.

import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAssetChart } from '../api/indexPerformanceApi';

type RangeKey = '1Y' | '2Y' | '3Y' | 'YTD';

const RANGES: RangeKey[] = ['1Y', '2Y', '3Y', 'YTD'];

// Compute the inclusive lower-bound date (YYYY-MM-DD) for a range. Because FMP
// dates are ISO YYYY-MM-DD, lexicographic string comparison equals chronology,
// so we can filter with a plain `>=` without parsing every row.
const computeCutoff = (range: RangeKey): string => {
  const now = new Date();
  if (range === 'YTD') {
    return `${now.getFullYear()}-01-01`;
  }
  const years = range === '1Y' ? 1 : range === '2Y' ? 2 : 3;
  const d = new Date(now);
  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().slice(0, 10);
};

const formatPrice = (v: number): string =>
  v.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatVolume = (v: number): string => {
  if (v >= 1e12) return `${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v}`;
};

// Compact axis tick for a multi-year span (e.g. "Jan 24").
const formatXTick = (str: string): string => {
  try {
    return format(parseISO(str), 'MMM yy');
  } catch {
    return str;
  }
};

interface AssetChartModalProps {
  symbol: string;
  name: string;
  onClose: () => void;
}

export const AssetChartModal = ({
  symbol,
  name,
  onClose,
}: AssetChartModalProps) => {
  const [range, setRange] = useState<RangeKey>('1Y');

  // The modal only mounts when a symbol is selected, so the hook fires on open.
  const { data, isLoading, isError, error } = useAssetChart(symbol);

  // Escape-to-close + body scroll lock (mirrors FXDetailPopup).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const historical = data?.historical ?? [];

  // Slice the full series down to the selected range window.
  const filtered = useMemo(() => {
    const cutoff = computeCutoff(range);
    return historical.filter((p) => p.date >= cutoff);
  }, [historical, range]);

  // FMP indices frequently report zero volume; hide the volume scale/bars then.
  const hasVolume = useMemo(
    () => filtered.some((p) => p.volume > 0),
    [filtered],
  );

  const isEmpty = !isLoading && !isError && filtered.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-5xl rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${name} price chart`}
      >
        <button
          type="button"
          className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors"
          onClick={onClose}
          aria-label="Close"
        >
          &#10005;
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pr-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {name}
            </h2>
            <p className="mt-0.5 text-sm font-medium text-blue-600 dark:text-blue-400">
              {symbol}
            </p>
          </div>

          {/* Range toggle: 1Y | 2Y | 3Y | YTD */}
          <div
            className="flex bg-gray-100 dark:bg-gray-800/60 rounded-lg p-1 border border-gray-200 dark:border-gray-700"
            role="tablist"
            aria-label="Chart range"
          >
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={range === r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  range === r
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Chart body */}
        <div className="relative mt-6 h-[420px] w-full text-gray-500 dark:text-gray-400">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              <span className="ml-3 font-medium">Loading chart…</span>
            </div>
          )}

          {isError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                {error instanceof Error
                  ? error.message
                  : 'Unable to load chart data for this asset.'}
              </div>
            </div>
          )}

          {isEmpty && (
            <div className="absolute inset-0 flex items-center justify-center text-sm">
              No historical data available for this range.
            </div>
          )}

          {!isLoading && !isError && filtered.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={filtered}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  className="stroke-gray-200 dark:stroke-gray-700"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatXTick}
                  tick={{ fontSize: 11, fill: 'currentColor' }}
                  tickLine={false}
                  minTickGap={40}
                />
                <YAxis
                  yAxisId="price"
                  domain={['auto', 'auto']}
                  tickFormatter={(v) => formatPrice(v as number)}
                  tick={{ fontSize: 11, fill: 'currentColor' }}
                  tickLine={false}
                  width={70}
                />
                {hasVolume && (
                  <YAxis
                    yAxisId="volume"
                    orientation="right"
                    domain={[0, (dataMax: number) => dataMax * 4]}
                    tickFormatter={(v) => formatVolume(v as number)}
                    tick={{ fontSize: 11, fill: 'currentColor' }}
                    tickLine={false}
                    width={52}
                  />
                )}
                <Tooltip
                  formatter={(value: number | string, name: string) =>
                    name === 'Volume'
                      ? formatVolume(value as number)
                      : formatPrice(value as number)
                  }
                  labelFormatter={(label: string) => {
                    try {
                      return format(parseISO(label), 'PP');
                    } catch {
                      return label;
                    }
                  }}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {hasVolume && (
                  <Bar
                    yAxisId="volume"
                    dataKey="volume"
                    name="Volume"
                    fill="#94a3b8"
                    opacity={0.3}
                    isAnimationActive={false}
                  />
                )}
                <Area
                  yAxisId="price"
                  type="monotone"
                  dataKey="close"
                  name="Close"
                  stroke="#2563eb"
                  fill="#93c5fd"
                  fillOpacity={0.3}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {!isLoading && !isError && filtered.length > 0 && !hasVolume && (
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            Volume is unavailable for this instrument (index feeds often report
            no turnover).
          </p>
        )}
      </div>
    </div>
  );
};

export default AssetChartModal;
